import { useEffect, useState, useCallback } from "react";
import { api, ROLE_LABELS, OWNER_LABELS } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Copy, ArrowsClockwise, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Household() {
  const { hid, isOperator, reload } = useHousehold();
  const [info, setInfo] = useState(null);
  const [audit, setAudit] = useState([]);
  const [open, setOpen] = useState(false);
  const [invite, setInvite] = useState({ email: "", role: "operator" });
  const [lastLink, setLastLink] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [ownerForm, setOwnerForm] = useState({ A: "Operator A", B: "Operator B", joint: "Joint" });
  const [savingOwners, setSavingOwners] = useState(false);

  const load = useCallback(() => {
    if (!hid) return;
    api.get(`/households/${hid}`).then((r) => {
      setInfo(r.data);
      setOwnerForm({ A: "Operator A", B: "Operator B", joint: "Joint", ...(r.data.owner_labels || {}) });
    }).catch(() => {});
    api.get(`/households/${hid}/audit`).then((r) => setAudit(r.data)).catch(() => {});
  }, [hid]);
  useEffect(() => { load(); }, [load]);

  const saveOwners = async () => {
    setSavingOwners(true);
    try {
      await api.put(`/households/${hid}/owner-labels`, ownerForm);
      toast.success("Owner names updated");
      await reload(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSavingOwners(false); }
  };

  const sendInvite = async () => {
    if (!invite.email.trim()) { toast.error("Email required"); return; }
    try {
      const res = await api.post(`/households/${hid}/invite`, invite);
      const link = `${window.location.origin}/accept-invite?token=${res.data.token}`;
      setLastLink(link);
      toast.success("Invite created — share the link below");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
  };

  const syncRE = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/households/${hid}/sync-real-estate`);
      toast.success(`Imported ${res.data.imported} of ${res.data.total_properties} properties as assets`);
      await reload();
    } catch (e) { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  if (!info) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Access &amp; continuity</p><h1 className="font-display text-4xl font-black tracking-tighter mt-1">{info.name}</h1></div>
        {isOperator && <div className="flex gap-2">
          <Button data-testid="sync-real-estate-button" variant="outline" onClick={syncRE} disabled={syncing} className="rounded-sm font-semibold"><ArrowsClockwise size={15} className="mr-1" /> {syncing ? "Importing…" : "Import Real Estate"}</Button>
          <Button data-testid="invite-member-button" onClick={() => { setLastLink(""); setOpen(true); }} className="rounded-sm font-semibold"><UserPlus size={16} weight="bold" className="mr-1" /> Invite</Button>
        </div>}
      </div>

      {/* Owner names */}
      <div className="bg-card border border-border rounded-sm p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-1">Owner Names</p>
        <p className="text-sm text-muted-foreground mb-4">These names label every asset, entity, and account by owner across the app.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label className="text-xs uppercase tracking-wider font-bold">Operator A</Label><Input data-testid="owner-a-input" disabled={!isOperator} value={ownerForm.A} onChange={(e) => setOwnerForm({ ...ownerForm, A: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. David" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-bold">Operator B</Label><Input data-testid="owner-b-input" disabled={!isOperator} value={ownerForm.B} onChange={(e) => setOwnerForm({ ...ownerForm, B: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Sarah" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-bold">Joint label</Label><Input data-testid="owner-joint-input" disabled={!isOperator} value={ownerForm.joint} onChange={(e) => setOwnerForm({ ...ownerForm, joint: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. David & Sarah" /></div>
        </div>
        {isOperator && <Button data-testid="save-owner-names-button" onClick={saveOwners} disabled={savingOwners} className="rounded-sm font-semibold mt-4">{savingOwners ? "Saving…" : "Save Owner Names"}</Button>}
      </div>

      {/* Members */}
      <div className="bg-card border border-border rounded-sm p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">Members</p>
        <div className="space-y-2">
          {info.members.map((m) => (
            <div key={m.id} data-testid={`member-${m.user_id}`} className="flex items-center justify-between border-b border-border last:border-0 pb-2 last:pb-0">
              <div><span className="font-semibold">{m.user_name || m.user_email}</span> <span className="text-xs text-muted-foreground">· {m.user_email}</span></div>
              <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${["operator", "executor"].includes(m.role) ? "bg-accent text-accent-foreground" : "bg-secondary text-secondary-foreground"}`}>{ROLE_LABELS[m.role] || m.role}{m.operator_label ? ` ${m.operator_label}` : ""}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Audit trail */}
      <div className="bg-card border border-border rounded-sm p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2"><ClockCounterClockwise size={15} /> Audit Trail</p>
        {audit.length === 0 ? <p className="text-sm text-muted-foreground">No activity yet.</p> : (
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {audit.map((a) => (
              <div key={a.id} className="text-sm flex items-center justify-between border-b border-border last:border-0 py-1.5">
                <span><span className="font-semibold">{a.user_email}</span> <span className="text-muted-foreground">{a.action} {a.target_type}{a.detail ? ` · ${a.detail}` : ""}</span></span>
                <span className="text-xs text-muted-foreground shrink-0 ml-2">{new Date(a.at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">Invite to Household</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">Email</Label><Input data-testid="invite-email-input" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="rounded-sm mt-1" placeholder="person@example.com" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Role</Label>
              <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}><SelectTrigger data-testid="invite-role-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <Button data-testid="send-invite-button" onClick={sendInvite} className="rounded-sm font-semibold w-full">Create Invite Link</Button>
            {lastLink && (
              <div className="bg-secondary rounded-sm p-3 text-xs break-all">
                <p className="font-bold uppercase tracking-wider text-muted-foreground mb-1">Share this link</p>
                <div className="flex items-center gap-2"><code data-testid="invite-link" className="flex-1">{lastLink}</code><button onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Copied"); }} className="hover:text-foreground"><Copy size={15} /></button></div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm w-full">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
